import { Component, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App";
import "./index.css";

/* ── Global error capture (fires before React renders, catches module failures) ── */
window.addEventListener("error", (ev) => {
  const root = document.getElementById("root");
  if (root && root.children.length === 0) {
    root.innerHTML = `<div style="font-family:monospace;padding:2rem;color:#f87171;background:#0a0a10;min-height:100vh">
      <h1 style="font-size:1.2rem;margin-bottom:.5rem">Load Error</h1>
      <pre style="white-space:pre-wrap;font-size:.8rem">${ev.message}</pre>
      <pre style="white-space:pre-wrap;font-size:.7rem;color:#6b7280;margin-top:.5rem">${ev.filename}:${ev.lineno}</pre>
    </div>`;
  }
});

window.addEventListener("unhandledrejection", (ev) => {
  const root = document.getElementById("root");
  if (root && root.children.length === 0) {
    root.innerHTML = `<div style="font-family:monospace;padding:2rem;color:#f87171;background:#0a0a10;min-height:100vh">
      <h1 style="font-size:1.2rem;margin-bottom:.5rem">Unhandled Promise Rejection</h1>
      <pre style="white-space:pre-wrap;font-size:.8rem">${String(ev.reason)}</pre>
    </div>`;
  }
});

/* ── React ErrorBoundary (catches render-time crashes) ── */
class RootErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      const err = this.state.error as Error;
      return (
        <div style={{ fontFamily: "monospace", padding: "2rem", color: "#f87171", background: "#0a0a10", minHeight: "100vh" }}>
          <h1 style={{ fontSize: "1.2rem", marginBottom: ".5rem" }}>React Error</h1>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: ".8rem" }}>{err.message}</pre>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: ".7rem", color: "#6b7280", marginTop: ".5rem" }}>{err.stack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById("root")!).render(
  <RootErrorBoundary>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </RootErrorBoundary>
);
